import React, { useEffect, useState } from "react";
import "./SearchBar.css";
import { SearchBarProps, SearchForm } from "./SearchBar.types";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useModal from "../modal/useModal";
import Modal from "../modal/Modal";
import ProfessionList from "../profession-list/ProfessionList";
import DegreeTypeList from "../degree-type-list/DegreeTypeList";
import AccreditationTypeList from "../accreditation-type-list/AccreditationTypeList";
import { DegreeTypes } from "../degree-type-list/DegreeTypeList.types";

enum statusLookup {
    "Accredited" = 1,
    "Distance" = 4,
    "Newly Accredited" = 2,
    "Letter of Review" = 5,
    "Withdrawn/Expired" = 3,
}

const SearchBar = (props: SearchBarProps) => {
    const { onSubmit: submitForm } = props;
    const [showingMap, setShowingMap] = useState(false);
    const [professionIds, setProfessionIds] = useState<number[]>([]);
    const [concentrationIds, setConcentrationIds] = useState<number[]>([]);
    const [addOnTrackIds, setAddOnTrackIds] = useState<number[]>([]);
    const [levelIds, setLevelIds] = useState<number[]>([]);
    const [statuses, setStatuses] = useState<number[]>([1]);
    const [status, setStatus] = useState<string>();
    const [profSwitch, setProfSwitch] = useState(false);
    const [searchText, setSearchText] = useState<string>("");
    const [isInternational, setIsInternational] = useState<boolean>(false);
    const [degreeTypes, setDegreeTypes] = useState<DegreeTypes>({
        degreeDiploma: true,
        degreeAssociate: true,
        degreeBaccalaureate: true,
        degreeCertificate: true,
        degreeMasters: true,
    });
    const {
        isShowing: professionsShowing,
        toggle: toggleProfessionsModal,
    } = useModal();
    const {
        isShowing: degreeTypesShowing,
        toggle: toggleDegreeTypesModal,
    } = useModal();
    const {
        isShowing: accreditationTypesShowing,
        toggle: toggleAccreditationTypesModal,
    } = useModal();

    const schema = yup
        .object({
            searchText: yup.string().notRequired(),
            isInternational: yup.boolean().required().default(false),
        })
        .defined();

    const { handleSubmit, register } = useForm<SearchForm>({
        resolver: yupResolver(schema),
    });

    const {
        handleSubmit: handleMobileSubmit,
        register: registerMobile,
    } = useForm<SearchForm>({
        resolver: yupResolver(schema),
    });

    const onSubmit = (formData: SearchForm) => {
        setSearchText(formData.searchText || "");
        setIsInternational(formData.isInternational || false);
        submitForm({
            ...formData,
            professionIds: professionIds,
            concentrations: concentrationIds,
            addOnTracks: addOnTrackIds,
            levels: levelIds,
            sortStatuses: statuses,
            ...degreeTypes,
        });
    };

    const toggleMap = () => {
        props.toggleMap();
        setShowingMap(!showingMap);
    };

    const updateProfessionProps = (profDef) => {
        setProfessionIds(profDef.professions);
        setLevelIds(profDef.levels);
        setConcentrationIds(profDef.concentrations);
        setAddOnTrackIds(profDef.addOnTracks);
        return !profSwitch;
    };

    useEffect(() => {
        submitForm({
            searchText: searchText,
            isInternational: isInternational,
            professionIds: professionIds,
            concentrations: concentrationIds,
            addOnTracks: addOnTrackIds,
            levels: levelIds,
            sortStatuses: statuses,
            ...degreeTypes,
        });
        setStatus(statusLookup[statuses[0]]);
    }, [
        degreeTypes,
        statuses,
        profSwitch
    ]);

    return (
        <>
            <form onSubmit={handleMobileSubmit(onSubmit)}>
                <div className="search-bar-sm">
                    <div className="input-group">
                        <div className="top-icon-input">
                            <div className="icon-input">
                                <div className="icon">
                                    <span className="material-icons">
                                        search
                                    </span>
                                </div>
                                <input
                                    name="searchText"
                                    type="text"
                                    placeholder="City, State, Institution Name"
                                    ref={registerMobile}
                                />
                            </div>
                        </div>
                        <div style={{ display: "flex" }}>
                            <div style={{ flex: 1 }}>
                                <button
                                    type="button"
                                    className="modal-trigger profession-modal-trigger"
                                    onClick={toggleProfessionsModal}
                                >
                                    Profession&nbsp;
                                    {professionIds?.length > 0 &&
                                        `(${professionIds?.length})`}
                                </button>
                            </div>
                            <div style={{ flex: 1 }}>
                                <button
                                    type="button"
                                    className="modal-trigger degree-modal-trigger "
                                    onClick={toggleDegreeTypesModal}
                                >
                                    Degree
                                    {Object.entries(degreeTypes).filter(
                                        (x) => x[1] === true
                                    ).length > 0 &&
                                        `(${
                                            Object.entries(degreeTypes).filter(
                                                (x) => x[1] === true
                                            ).length
                                        })`}
                                </button>
                            </div>
                            <div style={{ flex: 1 }}>
                                <button
                                    type="button"
                                    className="modal-trigger accreditation-modal-trigger"
                                    onClick={toggleAccreditationTypesModal}
                                >
                                    Accreditation
                                    {statuses?.length > 0 &&
                                        `(${statuses?.length})`}
                                </button>
                            </div>
                        </div>
                        <div>
                            <div style={{ height: "42px" }}>
                                <label className="filter-toggle">
                                    <input
                                        type="checkbox"
                                        name="isInternational"
                                        ref={registerMobile}
                                    />
                                    <span className="filter-btn">
                                        International
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div style={{ marginTop: "24px" }}>
                        <button className="submit-btn" type="submit">
                            Search
                        </button>
                    </div>
                </div>
            </form>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="search-bar-lg">
                    <div className="icon-input">
                        <div className="icon">
                            <span className="material-icons">search</span>
                        </div>
                        <input
                            name="searchText"
                            type="text"
                            placeholder="City, State, Country, Institution Name"
                            ref={register}
                        />
                    </div>
                    <div className="filter-container">
                        <button
                            type="button"
                            className="modal-trigger"
                            onClick={toggleProfessionsModal}
                        >
                            Profession&nbsp;
                            {professionIds?.length > 0 &&
                                `(${professionIds?.length})`}
                        </button>
                    </div>
                    <div className="filter-container">
                        <button
                            type="button"
                            className="modal-trigger"
                            onClick={toggleDegreeTypesModal}
                        >
                            Degree&nbsp;Type&nbsp;
                            {Object.entries(degreeTypes).filter(
                                (x) => x[1] === true
                            ).length > 0 &&
                                `(${
                                    Object.entries(degreeTypes).filter(
                                        (x) => x[1] === true
                                    ).length
                                })`}
                        </button>
                    </div>
                    <div className="filter-container">
                        <button
                            type="button"
                            className="modal-trigger"
                            onClick={toggleAccreditationTypesModal}
                        >
                            {status}
                        </button>
                    </div>
                    <div className="filter-container">
                        <label
                            className="filter-toggle"
                            style={{ width: "110px" }}
                        >
                            <input
                                type="checkbox"
                                name="isInternational"
                                ref={register}
                            />
                            <span className="filter-btn">International</span>
                        </label>
                    </div>
                    <div className="btn-container">
                        <button className="submit-btn" type="submit">
                            Search
                        </button>
                    </div>
                    <div className="print-container">
                        <button 
                        className="icon-button"
                        onClick={() => window.print()}
                        >
                            <span className="material-icons-outlined">
                                print
                            </span>
                        </button>
                    </div>
                    <div className="map-button-container">
                        <button
                            className="text-button"
                            onClick={() => toggleMap()}
                        >
                            {`${showingMap ? "Hide" : "Show"}`} Map
                        </button>
                    </div>
                </div>
            </form>

            <Modal
                isShowing={professionsShowing}
                hide={toggleProfessionsModal}
                headerText="What profession are you looking for?"
            >
                <ProfessionList
                    selectedProfessions={professionIds}
                    selectedConcentrations={concentrationIds}
                    selectedAddOnTracks={addOnTrackIds}
                    selectedLevels={levelIds}
                    onSubmit={(profDef) => {
                        setProfSwitch(updateProfessionProps(profDef));
                        toggleProfessionsModal();
                    }}
                />
            </Modal>

            <Modal
                isShowing={degreeTypesShowing}
                hide={toggleDegreeTypesModal}
                headerText="What type of degree are you looking for?"
            >
                <DegreeTypeList
                    selectedDegreeTypes={degreeTypes}
                    onSubmit={(degrees) => {
                        setDegreeTypes(degrees);
                        toggleDegreeTypesModal();
                    }}
                />
            </Modal>

            <Modal
                isShowing={accreditationTypesShowing}
                hide={toggleAccreditationTypesModal}
                headerText="Status"
            >
                <AccreditationTypeList
                    selectedAccreditationTypes={statuses}
                    onSubmit={(accreditationTypes) => {
                        setStatuses(accreditationTypes);
                        toggleAccreditationTypesModal();
                    }}
                />
            </Modal>
        </>
    );
};

export default SearchBar;
